import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import type {
  ParticipantDetailResponseDTO,
  ParticipantSearchRequest,
  ParticipantSearchResponse,
  ParticipantSearchResponseDTO,
  ParticipantUpdateRequestDTO,
} from '../../types/previdencia/previdenciaType';

export interface UsePrevidenciaParticipantReturn {
  participants: ParticipantSearchResponseDTO[];
  participantDetail: ParticipantDetailResponseDTO | null;
  isLoading: boolean;
  error: string | null;
  totalPages: number;
  totalElements: number;
  currentPage: number;
  clearError: () => void;
  searchParticipants: (params: ParticipantSearchRequest) => Promise<void>;
  getParticipantDetail: (participantEnrollmentId: number) => Promise<void>;
  updateParticipant: (request: ParticipantUpdateRequestDTO) => Promise<void>;
  clearParticipantDetail: () => void;
}

export const usePrevidenciaParticipant = (): UsePrevidenciaParticipantReturn => {
  const [participants, setParticipants] = useState<ParticipantSearchResponseDTO[]>([]);
  const [participantDetail, setParticipantDetail] = useState<ParticipantDetailResponseDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const searchParticipants = useCallback(async (params: ParticipantSearchRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: ParticipantSearchResponse = await apiService.participant.searchParticipants(params);
      setParticipants(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      setCurrentPage(response.pageNumber || 0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar participantes';
      setError(errorMessage);
      setParticipants([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getParticipantDetail = useCallback(async (participantEnrollmentId: number): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const detail: ParticipantDetailResponseDTO = await apiService.participant.getParticipantDetail(
        participantEnrollmentId
      );
      setParticipantDetail(detail);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar detalhes do participante';
      setError(errorMessage);
      setParticipantDetail(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateParticipant = useCallback(async (request: ParticipantUpdateRequestDTO): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const updated: ParticipantDetailResponseDTO = await apiService.participant.updateParticipant(request);
      setParticipantDetail(updated);
      // Atualizar também na lista se o participante estiver lá
      setParticipants((prev) =>
        prev.map((p) =>
          p.participantEnrollmentId === request.participantEnrollmentId
            ? {
              ...p,
              participantName: request.fullName,
              cpf: request.cpf,
              registrationNumber: request.registrationNumber,
            }
            : p
        )
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar participante';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearParticipantDetail = useCallback(() => {
    setParticipantDetail(null);
  }, []);

  return {
    participants,
    participantDetail,
    isLoading,
    error,
    totalPages,
    totalElements,
    currentPage,
    clearError,
    searchParticipants,
    getParticipantDetail,
    updateParticipant,
    clearParticipantDetail,
  };
};

